// src/components/admin/AdminClients.tsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../../api/axiosInstance'; // ← This is the key! Shared instance with token

const clientSchema = z.object({
    firstName: z.string().min(2, 'Meno musí mať aspoň 2 znaky'),
    lastName: z.string().min(2, 'Priezvisko musí mať aspoň 2 znaky'),
    // zod 4 removed the message argument from .string().email(); z.email() is the replacement.
    email: z.union([z.literal(''), z.email('Neplatný email')]),
    phone: z.string().optional(),
    street: z.string().optional(),
    city: z.string().optional(),
    postalCode: z.union([z.literal(''), z.string().regex(/^\d{5}$/, 'PSČ musí mať 5 číslic')]),
    birthNumber: z.string().optional(),
    note: z.string().optional(),
});

const emailSchema = z.object({
    subject: z.string().min(1, 'Predmet je povinný'),
    message: z.string().min(1, 'Správa je povinná'),
});

type ClientFormData = z.infer<typeof clientSchema>;
type EmailFormData = z.infer<typeof emailSchema>;

/** Must match mailbox.limits.max-upload-bytes on the server. */
const MAX_EMAIL_UPLOAD_BYTES = 20_000_000;

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' kB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

interface Client {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    street: string;
    city: string;
    postalCode: string;
    birthNumber: string | null;
    note: string | null;
    createdAt: string;
    updatedAt: string;
}

const API_BASE_URL = '/api/clients'; // ← Clean relative path (Vite/CRA handles proxy)

// Improved Styled Components with left alignment and compact table
const PageContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #fdfdfd 0%, #ffffff 100%);
    padding: 1.5rem;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const Container = styled.div`
    margin: 0 auto;
    position: relative;
`;

const Header = styled.div`
    margin-bottom: 2rem;
    text-align: left;

    @media (max-width: 768px) {
        margin-bottom: 1.5rem;
    }
`;

const Title = styled.h1`
    font-size: 2.5rem;
    font-weight: 800;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0 0 0.5rem 0;
    line-height: 1.2;
    text-align: left;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    color: #64748b;
    font-size: 1.1rem;
    margin: 0;
    font-weight: 500;
    text-align: left;

    @media (max-width: 768px) {
        font-size: 0.95rem;
    }
`;

const Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;

    @media (min-width: 1024px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
    }
`;

const SearchBox = styled.div`
    flex: 1;
    max-width: 400px;

    @media (max-width: 1024px) {
        max-width: 100%;
    }
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 1rem 1.2rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 1rem;
    background: white;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
    outline: none;
    transition: all 0.3s ease;

    &:focus {
        border-color: #990147;
        box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1);
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
    }
`;

const SelectedInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`;

const SelectedBadge = styled.div`
    background: linear-gradient(135deg, #990147, #cc3366);
    color: white;
    padding: 0.6rem 1.2rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.9rem;
    box-shadow: 0 4px 12px rgba(153, 1, 71, 0.25);
    white-space: nowrap;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 0.8rem;
    flex-wrap: wrap;
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' | 'danger', disabled?: boolean }>`
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
    opacity: ${p => p.disabled ? 0.5 : 1};
    white-space: nowrap;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 44px;

    ${p => p.variant === 'primary' && `
        background: linear-gradient(135deg, #990147, #cc3366);
        color: white;
        box-shadow: 0 4px 12px rgba(153, 1, 71, 0.25);
    `}

    ${p => p.variant === 'secondary' && `
        background: white;
        color: #475569;
        border: 1.5px solid #e2e8f0;
        box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    `}

    ${p => p.variant === 'danger' && `
        background: white;
        color: #dc2626;
        border: 1.5px solid #fee2e2;
        box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    `}

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: ${p => p.variant === 'primary'
                ? '0 6px 20px rgba(153, 1, 71, 0.3)'
                : '0 4px 12px rgba(0,0,0,0.1)'};
    }
`;

const TableSection = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    border: 1px solid #f1f5f9;
    overflow: hidden;
    margin-bottom: 1.5rem;
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
`;

// More compact table with smaller min-width
const Table = styled.table`
    width: 100%;
    min-width: 800px; // Reduced from 1000px
    border-collapse: collapse;
`;

const Th = styled.th`
    background: linear-gradient(135deg, #990147, #cc3366);
    color: white;
    padding: 1rem 0.8rem;
    text-align: left;
    font-weight: 600;
    font-size: 0.9rem;
    position: sticky;
    top: 0;

    &:first-child {
        width: 50px;
        text-align: center;
        padding: 1rem 0.5rem;
    }

    &:last-child {
        width: 150px;
        text-align: center;
        padding: 1rem 0.5rem;
    }
`;

const Td = styled.td`
    padding: 0.9rem 0.8rem;
    border-bottom: 1px solid #f8fafc;
    color: #334155;
    font-size: 0.85rem;
    line-height: 1.3;

    &:first-child {
        text-align: center;
        padding: 0.9rem 0.5rem;
    }

    &:last-child {
        text-align: center;
        padding: 0.9rem 0.5rem;
    }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #990147;
`;

const ActionBtn = styled(motion.button)<{ danger?: boolean }>`
    padding: 0.4rem 0.8rem;
    border: none;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    background: ${p => p.danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(153, 1, 71, 0.1)'};
    color: ${p => p.danger ? '#dc2626' : '#990147'};
    margin: 0 0.2rem;
    transition: all 0.2s ease;
    border: 1px solid ${p => p.danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(153, 1, 71, 0.2)'};

    &:hover {
        background: ${p => p.danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(153, 1, 71, 0.2)'};
        transform: translateY(-1px);
    }
`;

const Pagination = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    padding: 1.5rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);

    @media (min-width: 768px) {
        flex-direction: row;
        justify-content: space-between;
    }
`;

const PageInfo = styled.div`
    font-size: 0.9rem;
    color: #64748b;
    font-weight: 500;
    text-align: center;

    @media (min-width: 768px) {
        text-align: left;
    }
`;

const PageButtons = styled.div`
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
    justify-content: center;
`;

const PageButton = styled(motion.button)<{ active?: boolean }>`
    padding: 0.6rem 0.8rem;
    border: 1.5px solid ${p => p.active ? '#990147' : '#e2e8f0'};
    background: ${p => p.active ? '#990147' : 'white'};
    color: ${p => p.active ? 'white' : '#64748b'};
    border-radius: 8px;
    font-weight: 600;
    min-width: 40px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: ${p => p.active ? '#990147' : '#f8fafc'};
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
    }
`;

const AttachRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
    padding-top: 0.5rem;
    border-top: 1px solid #e2e8f0;
`;

const AttachLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;

    &:hover {
        border-color: #990147;
        color: #990147;
    }

    input {
        display: none;
    }
`;

const FileChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.6rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.78rem;
    color: #334155;
    max-width: 100%;

    button {
        border: none;
        background: none;
        color: #94a3b8;
        cursor: pointer;
        padding: 0;
        font-size: 0.8rem;

        &:hover {
            color: #dc2626;
        }
    }
`;

const FileChipName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
`;

const FileChipSize = styled.span`
    color: #94a3b8;
    flex-shrink: 0;
`;

const SenderNote = styled.p`
    margin: 0 0 1.2rem 0;
    padding: 0.7rem 0.9rem;
    background: #f8fafc;
    border-left: 3px solid #990147;
    border-radius: 8px;
    font-size: 0.8rem;
    color: #64748b;
    line-height: 1.5;

    strong {
        color: #334155;
    }
`;

// Modal Components
const ModalOverlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    overflow-y: auto;
`;

const Modal = styled(motion.div)`
    background: white;
    border-radius: 16px;
    padding: 2rem;
    width: 100%;
    max-width: 600px;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    position: relative;

    @media (max-width: 768px) {
        padding: 1.5rem;
        margin: 0.5rem;
    }
`;

const ModalTitle = styled.h2`
    font-size: 1.8rem;
    font-weight: 700;
    background: linear-gradient(135deg, #333, #990147);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0 0 1.5rem 0;
    text-align: left;
    line-height: 1.3;

    @media (max-width: 768px) {
        font-size: 1.5rem;
        margin-bottom: 1.2rem;
    }
`;

const Input = styled.input`
    width: 100%;
    padding: 0.9rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.9rem;
    background: white;
    transition: all 0.3s ease;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #990147;
        box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1);
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

const Textarea = styled.textarea`
    width: 100%;
    padding: 0.9rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.9rem;
    min-height: 100px;
    resize: vertical;
    background: white;
    transition: all 0.3s ease;
    box-sizing: border-box;
    font-family: inherit;

    &:focus {
        outline: none;
        border-color: #990147;
        box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1);
    }
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin: 1.2rem 0;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 0.8rem;
    }
`;

const FullWidthField = styled.div`
    grid-column: 1 / -1;
`;

const ErrorText = styled.p`
    color: #dc2626;
    font-size: 0.8rem;
    margin-top: 0.3rem;
    font-weight: 500;
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 0.8rem;
    justify-content: flex-end;
    margin-top: 2rem;
    flex-wrap: wrap;

    @media (max-width: 640px) {
        flex-direction: column;
    }
`;

const RecipientsList = styled.div`
    margin-bottom: 1.5rem;
    padding: 1.2rem;
    background: #f8fafc;
    border-radius: 8px;
    border-left: 3px solid #990147;
    max-height: 150px;
    overflow-y: auto;
`;

const RecipientItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
    font-size: 0.85rem;
    color: #475569;
    border-bottom: 1px solid #e2e8f0;

    &:last-child {
        border-bottom: none;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 3rem 1.5rem;
    color: #94a3b8;

    div {
        font-size: 3rem;
        margin-bottom: 0.8rem;
        opacity: 0.5;
    }

    h3 {
        color: #64748b;
        margin-bottom: 0.5rem;
        font-size: 1.1rem;
    }

    p {
        font-size: 0.9rem;
    }
`;

const AdminClients: React.FC = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [allSelected, setAllSelected] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [emailFiles, setEmailFiles] = useState<File[]>([]);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [editingClient, setEditingClient] = useState<Client | null>(null);

    const headerCheckboxRef = useRef<HTMLInputElement>(null);

    const { register, handleSubmit, reset, formState: { errors } } = useForm<ClientFormData>({ resolver: zodResolver(clientSchema) });
    const { register: registerEmail, handleSubmit: handleEmailSubmit, reset: resetEmail, formState: { errors: emailErrors } } = useForm<EmailFormData>({ resolver: zodResolver(emailSchema) });

    const fetchClients = async () => {
        try {
            const params = { page, size: 10, ...(search && { search }) };
            const res = await api.get(API_BASE_URL, { params }); // ← NOW USING api
            setClients(res.data.content);
            setTotalPages(res.data.totalPages);
            setTotalElements(res.data.totalElements);
            setAllSelected(selectedIds.size === res.data.totalElements && res.data.totalElements > 0);
        } catch (err) {
            toast.error('Nepodarilo sa načítať klientov');
        }
    };

    useEffect(() => {
        setPage(0);
    }, [search]);

    useEffect(() => {
        const timer = setTimeout(() => fetchClients(), 300);
        return () => clearTimeout(timer);
    }, [search, page]);

    const refreshAfterMutation = () => {
        setSelectedIds(new Set());
        setAllSelected(false);
        fetchClients();
    };

    const togglePage = () => {
        const pageIds = clients.map(c => c.id);
        const allChecked = pageIds.every(id => selectedIds.has(id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allChecked) pageIds.forEach(id => next.delete(id));
            else pageIds.forEach(id => next.add(id));
            return next;
        });
    };


    const onSubmit = async (data: ClientFormData) => {
        try {
            editingClient
                ? await api.put(`${API_BASE_URL}/${editingClient.id}`, data)
                : await api.post(API_BASE_URL, data);
            toast.success(editingClient ? 'Klient upravený' : 'Klient pridaný');
            reset();
            setIsModalOpen(false);
            setEditingClient(null);
            refreshAfterMutation();
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Chyba pri ukladaní');
        }
    };

    const addEmailFiles = (fileList: FileList | null) => {
        if (!fileList) return;
        const incoming: File[] = [];
        for (let i = 0; i < fileList.length; i++) {
            incoming.push(fileList[i]);
        }
        const next = emailFiles.concat(incoming);
        const total = next.reduce((sum, f) => sum + f.size, 0);
        if (total > MAX_EMAIL_UPLOAD_BYTES) {
            toast.error('Prílohy môžu mať spolu najviac 20 MB');
            return;
        }
        setEmailFiles(next);
    };

    const onEmailSubmit = async (data: EmailFormData) => {
        setSendingEmail(true);
        try {
            const form = new FormData();
            form.append(
                'payload',
                new Blob(
                    [JSON.stringify({
                        clientIds: Array.from(selectedIds),
                        subject: data.subject,
                        message: data.message,
                    })],
                    { type: 'application/json' }
                )
            );
            emailFiles.forEach((file) => form.append('attachments', file));

            // 'Content-Type': undefined is load-bearing — the shared axios instance defaults
            // to application/json, and with FormData that default survives, so the multipart
            // boundary is never generated and the backend answers 415.
            const res = await api.post(`${API_BASE_URL}/send-email`, form, {
                headers: { 'Content-Type': undefined },
            });

            toast.success(res.data?.message || `Email odoslaný ${selectedIds.size} klientom`);
            setIsEmailModalOpen(false);
            setEmailFiles([]);
            resetEmail();
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Chyba pri odosielaní');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleDelete = async (id: number) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('Naozaj chceš vymazať tohto klienta?')) return;
        try {
            await api.delete(`${API_BASE_URL}/${id}`);
            toast.success('Klient odstránený');
            setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            fetchClients();
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Chyba');
        }
    };

    const selectAllClients = async () => {
        if (allSelected) {
            setSelectedIds(new Set());
            setAllSelected(false);
            return;
        }
        try {
            toast.loading('Označujem všetkých...');
            const res = await api.get(`${API_BASE_URL}/all-ids`, { params: { search } });
            setSelectedIds(new Set(res.data));
            setAllSelected(true);
            toast.dismiss();
            toast.success(`Označených všetkých ${res.data.length} klientov`);
        } catch {
            toast.dismiss();
            toast.error('Nepodarilo sa označiť všetkých');
        }
    };

    const openModal = (client?: Client) => {
        if (client) {
            setEditingClient(client);
            reset({
                firstName: client.firstName,
                lastName: client.lastName,
                email: client.email,
                phone: client.phone || '',
                street: client.street,
                city: client.city,
                postalCode: client.postalCode,
                birthNumber: client.birthNumber || '',
                note: client.note || '',
            });
        } else {
            setEditingClient(null);
            reset();
        }
        setIsModalOpen(true);
    };

    const openEmailModal = () => {
        if (selectedIds.size === 0) return;
        resetEmail();
        setIsEmailModalOpen(true);
    };

    const toggleClient = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };


    const pageIds = clients.map(c => c.id);
    const allPageChecked = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
    const somePageChecked = pageIds.some(id => selectedIds.has(id));

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = somePageChecked && !allPageChecked;
        }
    }, [somePageChecked, allPageChecked]);

    const renderPagination = () => {
        const pages = [];
        for (let i = Math.max(0, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) {
            pages.push(
                <PageButton
                    key={i}
                    active={i === page}
                    onClick={() => setPage(i)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    {i + 1}
                </PageButton>
            );
        }
        return (
            <Pagination>
                <PageInfo>Zobrazených {clients.length} z {totalElements} klientov</PageInfo>
                <PageButtons>
                    <PageButton
                        onClick={() => setPage(0)}
                        disabled={page === 0}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        First
                    </PageButton>
                    <PageButton
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Prev
                    </PageButton>
                    {pages}
                    <PageButton
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Next
                    </PageButton>
                    <PageButton
                        onClick={() => setPage(totalPages - 1)}
                        disabled={page >= totalPages - 1}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Last
                    </PageButton>
                </PageButtons>
            </Pagination>
        );
    };

    return (
        <PageContainer>
            <Container>
                <Header>
                    <Title>Správa Klientov</Title>
                    <Subtitle>Prehľad a správa všetkých vašich klientov</Subtitle>
                </Header>

                <Controls>
                    <SearchBox>
                        <SearchInput
                            type="text"
                            placeholder="Hľadať podľa mena, emailu alebo mesta..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </SearchBox>

                    <ButtonGroup>
                        <SelectedInfo>
                            {selectedIds.size > 0 && (
                                <SelectedBadge>
                                    Vybratých: {selectedIds.size}{allSelected && ' (všetci)'}
                                </SelectedBadge>
                            )}
                        </SelectedInfo>
                        <ButtonRow>
                            <Button
                                variant="danger"
                                onClick={selectAllClients}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {allSelected ? 'Zrušiť výber' : 'Označiť všetkých'}
                            </Button>
                            <Button
                                variant="secondary"
                                disabled={selectedIds.size === 0}
                                onClick={openEmailModal}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Poslať email
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => openModal()}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                + Pridať klienta
                            </Button>
                        </ButtonRow>
                    </ButtonGroup>
                </Controls>

                <TableSection>
                    <TableContainer>
                        <Table>
                            <thead>
                            <tr>
                                <Th>
                                    <Checkbox
                                        checked={allPageChecked}
                                        ref={headerCheckboxRef}
                                        onChange={togglePage}
                                    />
                                </Th>
                                <Th>Meno</Th>
                                <Th>Email</Th>
                                <Th>Telefón</Th>
                                <Th>Adresa</Th>
                                <Th>Akcie</Th>
                            </tr>
                            </thead>
                            <tbody>
                            {clients.length === 0 ? (
                                <tr>
                                    <Td colSpan={6}>
                                        <EmptyState>
                                            <div>📝</div>
                                            <h3>Žiadni klienti</h3>
                                            <p>Neboli nájdení žiadni klienti</p>
                                        </EmptyState>
                                    </Td>
                                </tr>
                            ) : (
                                <AnimatePresence>
                                    {clients.map(client => (
                                        <motion.tr
                                            key={client.id}
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -5 }}
                                            transition={{ duration: 0.15 }}
                                        >
                                            <Td>
                                                <Checkbox
                                                    checked={selectedIds.has(client.id)}
                                                    onChange={() => toggleClient(client.id)}
                                                />
                                            </Td>
                                            <Td>
                                                <strong>{client.firstName} {client.lastName}</strong>
                                            </Td>
                                            <Td>{client.email}</Td>
                                            <Td>{client.phone || '—'}</Td>
                                            <Td>{client.street}, {client.postalCode} {client.city}</Td>
                                            <Td>
                                                <ActionBtn
                                                    onClick={() => openModal(client)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    Upraviť
                                                </ActionBtn>
                                                <ActionBtn
                                                    danger
                                                    onClick={() => handleDelete(client.id)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    Zmazať
                                                </ActionBtn>
                                            </Td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            )}
                            </tbody>
                        </Table>
                    </TableContainer>
                </TableSection>

                {renderPagination()}

                {/* Add/Edit Client Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <ModalOverlay
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                        >
                            <Modal
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                            >
                                <ModalTitle>
                                    {editingClient ? 'Upraviť klienta' : 'Nový klient'}
                                </ModalTitle>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <FormGrid>
                                        <div>
                                            <Input placeholder="Meno *" {...register('firstName')} />
                                            {errors.firstName && <ErrorText>{errors.firstName.message}</ErrorText>}
                                        </div>
                                        <div>
                                            <Input placeholder="Priezvisko *" {...register('lastName')} />
                                            {errors.lastName && <ErrorText>{errors.lastName.message}</ErrorText>}
                                        </div>
                                        <div>
                                            <Input placeholder="Email" type="email" {...register('email')} />
                                            {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                                        </div>
                                        <div>
                                            <Input placeholder="Telefón" {...register('phone')} />
                                        </div>
                                        <div>
                                            <Input placeholder="Ulica a číslo" {...register('street')} />
                                            {errors.street && <ErrorText>{errors.street.message}</ErrorText>}
                                        </div>
                                        <div>
                                            <Input placeholder="Mesto" {...register('city')} />
                                            {errors.city && <ErrorText>{errors.city.message}</ErrorText>}
                                        </div>
                                        <div>
                                            <Input placeholder="PSČ" {...register('postalCode')} />
                                            {errors.postalCode && <ErrorText>{errors.postalCode.message}</ErrorText>}
                                        </div>
                                        <div>
                                            <Input placeholder="Rodné číslo" {...register('birthNumber')} />
                                        </div>
                                        <FullWidthField>
                                            <Textarea placeholder="Poznámka" {...register('note')} />
                                        </FullWidthField>
                                    </FormGrid>
                                    <ModalButtons>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            Zrušiť
                                        </Button>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            {editingClient ? 'Uložiť zmeny' : 'Pridať klienta'}
                                        </Button>
                                    </ModalButtons>
                                </form>
                            </Modal>
                        </ModalOverlay>
                    )}
                </AnimatePresence>

                {/* Email Modal */}
                <AnimatePresence>
                    {isEmailModalOpen && (
                        <ModalOverlay
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsEmailModalOpen(false)}
                        >
                            <Modal
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                                style={{ maxWidth: '650px' }}
                            >
                                <ModalTitle>
                                    Poslať email {selectedIds.size} klientom
                                </ModalTitle>
                                <RecipientsList>
                                    <strong style={{ display: 'block', marginBottom: '0.8rem', color: '#334155' }}>
                                        Príjemcovia ({selectedIds.size}):
                                    </strong>
                                    {clients.filter(c => selectedIds.has(c.id)).map(c => (
                                        <RecipientItem key={c.id}>
                                            • {c.firstName} {c.lastName} ({c.email})
                                        </RecipientItem>
                                    ))}
                                </RecipientsList>
                                <form onSubmit={handleEmailSubmit(onEmailSubmit)}>
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <Input placeholder="Predmet emailu *" {...registerEmail('subject')} />
                                        {emailErrors.subject && <ErrorText>{emailErrors.subject.message}</ErrorText>}
                                    </div>
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <Textarea
                                            placeholder="Text správy *"
                                            {...registerEmail('message')}
                                            style={{ minHeight: '150px' }}
                                        />
                                        {emailErrors.message && <ErrorText>{emailErrors.message.message}</ErrorText>}
                                    </div>

                                    <AttachRow>
                                        <AttachLabel>
                                            📎 Priložiť súbor
                                            <input
                                                type="file"
                                                multiple
                                                onChange={(e) => {
                                                    addEmailFiles(e.target.files);
                                                    e.target.value = ''; // allow re-picking the same file
                                                }}
                                            />
                                        </AttachLabel>

                                        {emailFiles.map((file, index) => (
                                            <FileChip key={file.name + index}>
                                                <FileChipName>{file.name}</FileChipName>
                                                <FileChipSize>{formatFileSize(file.size)}</FileChipSize>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setEmailFiles(emailFiles.filter((_, i) => i !== index))
                                                    }
                                                    aria-label={`Odstrániť ${file.name}`}
                                                >
                                                    ✕
                                                </button>
                                            </FileChip>
                                        ))}
                                    </AttachRow>

                                    <SenderNote>
                                        Odosiela sa z <strong>ambulancia@sonabelanska.sk</strong>. Každý klient
                                        dostane samostatnú správu, takže nikto nevidí adresy ostatných.
                                    </SenderNote>

                                    <ModalButtons>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            disabled={sendingEmail}
                                            onClick={() => setIsEmailModalOpen(false)}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            Zrušiť
                                        </Button>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={sendingEmail}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            {sendingEmail ? 'Odosielam…' : 'Odoslať email'}
                                        </Button>
                                    </ModalButtons>
                                </form>
                            </Modal>
                        </ModalOverlay>
                    )}
                </AnimatePresence>
            </Container>
        </PageContainer>
    );
};

export default AdminClients;

